import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useBranch } from '../contexts/BranchContext'
import { subscriptionService, customerService } from '../services/firebase'
import { CreditCard, Plus, Users, Calendar, DollarSign, Search, Eye, Pause, Play, X, CheckCircle, AlertTriangle, Edit, RefreshCw, Filter, Package } from 'lucide-react'
import LoadingSpinner from '../components/LoadingSpinner'
import toast from 'react-hot-toast'
import CustomDropdown from '../components/CustomDropdown'
import { cn } from '../utils/cn'
import { subscriptionPlanService } from '@/services/subscriptionPlanService'
import { washPackages as staticWashPackages } from '@/config/washCatalog'
import { washCatalogService } from '@/services/washCatalogService'

export default function SubscriptionsPage() {
  const { user } = useAuth()
  const { currentBranch, isViewingAllBranches, getActualBranches } = useBranch()
  const [subscriptions, setSubscriptions] = useState([])
  const [plans, setPlans] = useState([])
  const [customers, setCustomers] = useState([])
  const [filteredSubscriptions, setFilteredSubscriptions] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [dateFilter, setDateFilter] = useState('')
  const [planFilter, setPlanFilter] = useState('all')
  const [showAddModal, setShowAddModal] = useState(false)
  const [showPlanModal, setShowPlanModal] = useState(false)
  const [editingPlanId, setEditingPlanId] = useState(null)
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [selectedSubscription, setSelectedSubscription] = useState(null)
  const [subscriptionBranchId, setSubscriptionBranchId] = useState('')
  const [newSubscription, setNewSubscription] = useState({
    customerId: '',
    planType: 'monthly',
    amount: 500,
    startDate: new Date().toISOString().split('T')[0],
    washesIncluded: 'unlimited',
    isActive: true
  })

  const [newPlan, setNewPlan] = useState({
    name: '',
    code: '',
    amount: 0,
    washesIncluded: 'unlimited',
    durationDays: 30,
    eligibleServiceIds: [],
    eligibleTierIds: [],
    isActive: true,
  })
  const [washServices, setWashServices] = useState(staticWashPackages)

  useEffect(() => {
    ;(async () => {
      try {
        await washCatalogService.ensureInitialized()
        const doc = await washCatalogService.get()
        if (doc?.packages?.length) setWashServices(doc.packages)
      } catch (e) {
        console.error(e)
      }
    })()
  }, [])

  useEffect(() => {
    loadData()
  }, [currentBranch])

  useEffect(() => {
    if (!currentBranch) return
    if (!isViewingAllBranches()) {
      setSubscriptionBranchId(currentBranch.id)
      return
    }
    const branches = getActualBranches()
    if (branches?.length && !subscriptionBranchId) setSubscriptionBranchId(branches[0].id)
  }, [currentBranch, isViewingAllBranches, getActualBranches, subscriptionBranchId])

  useEffect(() => {
    filterSubscriptions()
  }, [subscriptions, searchTerm, statusFilter, dateFilter, planFilter])

  const loadData = async () => {
    if (!currentBranch) return
    
    try {
      setLoading(true)
      const branchId = isViewingAllBranches() ? 'all-branches' : currentBranch.id
      const [subscriptionData, customerData, planData] = await Promise.all([
        subscriptionService.getActiveSubscriptions(branchId),
        customerService.getAllCustomers(),
        subscriptionPlanService.listPlans(branchId)
      ])
      
      setSubscriptions(subscriptionData)
      setCustomers(customerData)
      setPlans(planData)
    } catch (error) {
      console.error('Error loading data:', error)
      toast.error('Failed to load subscriptions')
    } finally {
      setLoading(false)
    }
  }

  const filterSubscriptions = () => {
    let filtered = subscriptions

    if (searchTerm) {
      filtered = filtered.filter(subscription => {
        const customer = customers.find(c => c.id === subscription.customerId)
        return customer?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
               customer?.phone?.includes(searchTerm) ||
               subscription.planType.toLowerCase().includes(searchTerm.toLowerCase())
      })
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(s => {
        const status = getSubscriptionStatus(s);
        if (statusFilter === 'active') return s.isActive && new Date(s.expiryDate) > new Date();
        if (statusFilter === 'expired') return new Date(s.expiryDate) <= new Date();
        if (statusFilter === 'paused') return !s.isActive;
        if (statusFilter === 'cancelled') return s.status === 'cancelled';
        if (statusFilter === 'pending') return s.status === 'pending';
        return true; // Default to all if statusFilter is not recognized
      });
    }

    if (dateFilter) {
      filtered = filtered.filter(s => new Date(s.startDate) >= new Date(dateFilter));
    }

    if (planFilter !== 'all') {
      filtered = filtered.filter(s => s.planType === planFilter);
    }

    setFilteredSubscriptions(filtered)
  }

  const getPlanLabel = (planCode) => {
    const plan = plans.find((p) => p.code === planCode) || plans.find((p) => p.id === planCode)
    return plan?.name || planCode
  }

  const branchLabel = (branchId) => {
    if (!branchId) return 'All branches'
    const b = getActualBranches().find((x) => x.id === branchId)
    return b?.name || branchId
  }

  const plansForSelectedBranch = () => {
    if (!isViewingAllBranches()) return plans
    const bid = subscriptionBranchId
    return plans.filter((p) => p.isActive !== false).filter((p) => !p.branchId || p.branchId === bid)
  }

  const handleAddSubscription = async (e) => {
    e.preventDefault()
    
    try {
      const customer = customers.find(c => c.id === newSubscription.customerId)
      if (!customer) {
        toast.error('Please select a valid customer')
        return
      }

      const startDate = new Date(newSubscription.startDate)
      let expiryDate = new Date(startDate)
      
      const planList = isViewingAllBranches() ? plansForSelectedBranch() : plans.filter((p) => p.isActive !== false)
      const plan = planList.find((p) => p.code === newSubscription.planType || p.id === newSubscription.planType)
      if (plan?.durationDays) {
        expiryDate = new Date(startDate.getTime() + Number(plan.durationDays) * 24 * 60 * 60 * 1000)
      } else {
        // Fallback for legacy values
        switch (newSubscription.planType) {
          case 'weekly':
            expiryDate.setDate(expiryDate.getDate() + 7)
            break
          case 'monthly':
            expiryDate.setMonth(expiryDate.getMonth() + 1)
            break
          case 'quarterly':
            expiryDate.setMonth(expiryDate.getMonth() + 3)
            break
          default:
            expiryDate.setMonth(expiryDate.getMonth() + 12)
            break
        }
      }

      const subscriptionData = {
        ...newSubscription,
        branchId: isViewingAllBranches() ? subscriptionBranchId : currentBranch.id,
        branchName: isViewingAllBranches() ? branchLabel(subscriptionBranchId) : currentBranch.name,
        customerName: customer.name,
        customerPhone: customer.phone,
        startDate: startDate,
        expiryDate: expiryDate,
        washesUsed: 0,
        amount: parseFloat(newSubscription.amount)
      }
      
      await subscriptionService.createSubscription(subscriptionData)
      
      // Update customer to mark as having active subscription
      await customerService.updateCustomer(customer.id, {
        hasActiveSubscription: true
      })
      
      toast.success('Subscription created successfully')
      setShowAddModal(false)
      setNewSubscription({
        customerId: '',
        planType: 'monthly',
        amount: 500,
        startDate: new Date().toISOString().split('T')[0],
        washesIncluded: 'unlimited',
        isActive: true
      })
      loadData()
    } catch (error) {
      console.error('Error creating subscription:', error)
      toast.error('Failed to create subscription')
    }
  }

  const resetPlanForm = () => {
    setEditingPlanId(null)
    setNewPlan({
      name: '',
      code: '',
      amount: 0,
      washesIncluded: 'unlimited',
      durationDays: 30,
      eligibleServiceIds: [],
      eligibleTierIds: [],
      isActive: true,
    })
  }

  const openEditPlan = (plan) => {
    setEditingPlanId(plan.id)
    setNewPlan({
      name: plan.name || '',
      code: plan.code || '',
      amount: Number(plan.amount || 0),
      washesIncluded: plan.washesIncluded ?? 'unlimited',
      durationDays: Number(plan.durationDays || 0),
      eligibleServiceIds: Array.isArray(plan.eligibleServiceIds) ? plan.eligibleServiceIds : [],
      eligibleTierIds: Array.isArray(plan.eligibleTierIds) ? plan.eligibleTierIds : [],
      isActive: plan.isActive !== false,
    })
    setShowPlanModal(true)
  }

  const handleSavePlan = async (e) => {
    e.preventDefault()
    if (!currentBranch) return
    if (!newPlan.name.trim() || !newPlan.code.trim()) {
      toast.error('Plan name and code are required')
      return
    }
    try {
      if (editingPlanId) {
        await subscriptionPlanService.updatePlan(editingPlanId, newPlan)
        toast.success('Subscription package updated')
      } else {
        await subscriptionPlanService.createPlan({
          ...newPlan,
          branchId: currentBranch.id,
        })
        toast.success('Subscription package created')
      }
      setShowPlanModal(false)
      resetPlanForm()
      loadData()
    } catch (error) {
      console.error(error)
      toast.error(editingPlanId ? 'Failed to update package' : 'Failed to create package')
    }
  }

  const handleDeletePlan = async (plan) => {
    if (!plan?.id) return
    const ok = window.confirm(`Delete subscription package "${plan.name}"? This cannot be undone.`)
    if (!ok) return
    try {
      await subscriptionPlanService.removePlan(plan.id)
      toast.success('Package deleted')
      loadData()
    } catch (e) {
      console.error(e)
      toast.error('Failed to delete package')
    }
  }

  const handleToggleSubscription = async (subscriptionId, currentStatus) => {
    try {
      await subscriptionService.updateSubscription(subscriptionId, {
        isActive: !currentStatus
      })
      toast.success(`Subscription ${!currentStatus ? 'activated' : 'paused'} successfully`)
      loadData()
    } catch (error) {
      console.error('Error updating subscription:', error)
      toast.error('Failed to update subscription')
    }
  }

  const handleCancelSubscription = async (subscriptionId) => {
    if (!window.confirm('Are you sure you want to cancel this subscription?')) return
    
    try {
      await subscriptionService.cancelSubscription(subscriptionId)
      toast.success('Subscription cancelled successfully')
      loadData()
    } catch (error) {
      console.error('Error cancelling subscription:', error)
      toast.error('Failed to cancel subscription')
    }
  }

  const handleDeleteSubscription = async (subscriptionId) => {
    if (!window.confirm('Delete this subscription permanently? This cannot be undone.')) return
    try {
      await subscriptionService.deleteSubscription(subscriptionId)
      toast.success('Subscription deleted')
      loadData()
    } catch (error) {
      console.error('Error deleting subscription:', error)
      toast.error('Failed to delete subscription')
    }
  }

  const handleEditSubscription = (subscription) => {
    setSelectedSubscription(subscription);
    setShowAddModal(true); // Reusing add modal for edit
    setNewSubscription({
      customerId: subscription.customerId,
      planType: subscription.planType,
      amount: subscription.amount,
      startDate: new Date(subscription.startDate).toISOString().split('T')[0],
      washesIncluded: subscription.washesIncluded,
      isActive: subscription.isActive
    });
  };

  const handleRenewSubscription = async (subscriptionId) => {
    const subscription = subscriptions.find(s => s.id === subscriptionId);
    if (!subscription) return;

    const startDate = new Date(subscription.startDate);
    let expiryDate = new Date(startDate);

    switch (subscription.planType) {
      case 'weekly':
        expiryDate.setDate(expiryDate.getDate() + 7);
        break;
      case 'monthly':
        expiryDate.setMonth(expiryDate.getMonth() + 1);
        break;
      case 'quarterly':
        expiryDate.setMonth(expiryDate.getMonth() + 3);
        break;
      case 'prepaid-10':
      case 'prepaid-20':
        expiryDate.setMonth(expiryDate.getMonth() + 12); // 1 year validity for prepaid
        break;
    }

    try {
      await subscriptionService.updateSubscription(subscriptionId, {
        startDate: startDate,
        expiryDate: expiryDate,
        washesUsed: 0, // Reset washes used
        isActive: true // Ensure it's active after renewal
      });
      toast.success('Subscription renewed successfully');
      loadData();
    } catch (error) {
      console.error('Error renewing subscription:', error);
      toast.error('Failed to renew subscription');
    }
  };

  const getSubscriptionStatus = (subscription) => {
    const now = new Date()
    const expiryDate = new Date(subscription.expiryDate)
    
    if (!subscription.isActive) return 'Paused'
    if (expiryDate <= now) return 'Expired'
    if (expiryDate <= new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000)) return 'Expiring Soon'
    return 'Active'
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'bg-green-100 text-green-800'
      case 'Paused': return 'bg-yellow-100 text-yellow-800'
      case 'Expired': return 'bg-red-100 text-red-800'
      case 'Expiring Soon': return 'bg-orange-100 text-orange-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getTotalStats = () => {
    const activeSubscriptions = subscriptions.filter(s => 
      s.isActive && new Date(s.expiryDate) > new Date()
    )
    const totalRevenue = activeSubscriptions.reduce((sum, s) => sum + s.amount, 0)
    const expiringSoon = subscriptions.filter(s => {
      const expiryDate = new Date(s.expiryDate)
      const weekFromNow = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
      return s.isActive && expiryDate <= weekFromNow && expiryDate > new Date()
    })

    return {
      total: subscriptions.length,
      active: activeSubscriptions.length,
      revenue: totalRevenue,
      expiringSoon: expiringSoon.length
    }
  }

  const getActiveSubscriptions = () => {
    return subscriptions.filter(s => s.isActive && new Date(s.expiryDate) > new Date());
  };

  const getMonthlyRevenue = () => {
    return getActiveSubscriptions().reduce((sum, s) => sum + s.amount, 0);
  };

  const getExpiringSubscriptions = () => {
    const now = new Date();
    return subscriptions.filter(s => {
      const expiryDate = new Date(s.expiryDate);
      const weekFromNow = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
      return s.isActive && expiryDate <= weekFromNow && expiryDate > now;
    });
  };

  const getUniquePlans = () => {
    const uniquePlans = new Set();
    subscriptions.forEach(s => {
      uniquePlans.add(s.planType);
    });
    return Array.from(uniquePlans);
  };

  const getSubscriptionProgress = (subscription) => {
    const startDate = new Date(subscription.startDate);
    const expiryDate = new Date(subscription.expiryDate);
    const now = new Date();

    if (expiryDate <= now) return 100; // 100% if expired

    const totalDays = (expiryDate - startDate) / (1000 * 60 * 60 * 24);
    const daysElapsed = (now - startDate) / (1000 * 60 * 60 * 24);

    return Math.min(100, (daysElapsed / totalDays) * 100);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    )
  }

  const stats = getTotalStats()

  return (
    <div className="min-h-screen bg-background pb-8">
      <div className="max-w-7xl mx-auto px-4 py-8 space-y-8">
        
        {/* Header */}
        <div className="text-center space-y-4">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-primary/10 text-primary mb-4">
            <CreditCard className="h-10 w-10" />
          </div>
          <h1 className="text-3xl font-semibold tracking-tight text-foreground">
            Subscription Management
          </h1>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Manage customer subscriptions and recurring payments
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="rounded-xl border border-border/80 bg-card shadow-sm p-6 hover:shadow-xl transition-all duration-300">
            <div className="flex items-center">
              <div className="w-12 h-12 rounded-xl bg-violet-500/10 flex items-center justify-center mr-4 text-violet-700 dark:text-violet-400">
                <CreditCard className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Total Subscriptions</p>
                <p className="text-2xl font-bold text-gray-900">{subscriptions.length}</p>
              </div>
            </div>
          </div>
          
          <div className="rounded-xl border border-border/80 bg-card shadow-sm p-6 hover:shadow-xl transition-all duration-300">
            <div className="flex items-center">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center mr-4 text-emerald-700 dark:text-emerald-400">
                <CheckCircle className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Active</p>
                <p className="text-2xl font-bold text-gray-900">{getActiveSubscriptions().length}</p>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-border/80 bg-card shadow-sm p-6 hover:shadow-xl transition-all duration-300">
            <div className="flex items-center">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mr-4 text-primary">
                <DollarSign className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Monthly Revenue</p>
                <p className="text-2xl font-bold text-gray-900">R{getMonthlyRevenue().toFixed(2)}</p>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-border/80 bg-card shadow-sm p-6 hover:shadow-xl transition-all duration-300">
            <div className="flex items-center">
              <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center mr-4 text-amber-800 dark:text-amber-400">
                <AlertTriangle className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Expiring Soon</p>
                <p className="text-2xl font-bold text-gray-900">{getExpiringSubscriptions().length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">
          <button
            onClick={() => setShowAddModal(true)}
            className="w-full sm:w-auto rounded-xl bg-primary px-8 py-4 font-semibold text-primary-foreground shadow-sm transition hover:bg-primary/90"
          >
            <Plus className="h-5 w-5 inline mr-2 group-hover:rotate-90 transition-transform duration-300" />
            Add New Subscription
          </button>
          <button
            onClick={() => setShowPlanModal(true)}
            className="w-full sm:w-auto rounded-xl bg-white px-8 py-4 font-semibold text-gray-900 shadow-sm border border-gray-200 transition hover:bg-gray-50"
          >
            <Package className="h-5 w-5 inline mr-2" />
            Add Package
          </button>
        </div>

        {/* Subscription Packages */}
        <div className="rounded-xl border border-border/80 bg-card shadow-sm p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-foreground">Subscription packages</h2>
              <p className="text-sm text-muted-foreground">
                These are the packages that show in POS → Subscriptions.
              </p>
            </div>
            <button
              onClick={() => setShowPlanModal(true)}
              className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground shadow-sm transition hover:bg-primary/90"
            >
              <Plus className="h-4 w-4 inline mr-2" />
              Add package
            </button>
          </div>

          <div className="mt-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {plans.length === 0 ? (
              <div className="md:col-span-2 lg:col-span-3 rounded-xl border border-border/80 bg-muted/30 p-6 text-center">
                <p className="text-sm text-muted-foreground">No packages yet. Click “Add package”.</p>
              </div>
            ) : (
              plans.map((p) => (
                <div key={p.id} className="rounded-xl border border-border/80 bg-card p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-semibold text-foreground">{p.name}</p>
                      <p className="text-xs text-muted-foreground break-all">{p.code || p.id}</p>
                      {isViewingAllBranches() && (
                        <p className="mt-1 text-[11px] text-muted-foreground">
                          Branch: <span className="font-medium text-foreground/90">{branchLabel(p.branchId)}</span>
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => openEditPlan(p)}
                        className="p-2 rounded-xl hover:bg-muted transition"
                        title="Edit package"
                        aria-label="Edit package"
                      >
                        <Edit className="h-4 w-4 text-gray-700" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeletePlan(p)}
                        className="p-2 rounded-xl hover:bg-red-50 transition"
                        title="Delete package"
                        aria-label="Delete package"
                      >
                        🗑️
                      </button>
                      <span
                        className={cn(
                          'inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium',
                          p.isActive === false
                            ? 'bg-muted text-muted-foreground'
                            : 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
                        )}
                      >
                        {p.isActive === false ? 'Inactive' : 'Active'}
                      </span>
                    </div>
                  </div>

                  <div className="mt-3 space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Price (VAT incl)</span>
                      <span className="font-medium tabular-nums">R{Number(p.amount || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Duration</span>
                      <span className="font-medium tabular-nums">
                        {Number(p.durationDays || 0) > 0 ? `${Number(p.durationDays)} days` : 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Washes</span>
                      <span className="font-medium tabular-nums">
                        {p.washesIncluded === 'unlimited' ? 'Unlimited' : Number(p.washesIncluded || 0)}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="rounded-xl border border-border/80 bg-card shadow-sm p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search subscriptions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-white/50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
              />
            </div>
            <div className="relative">
              <Filter className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-white/50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 appearance-none"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="expired">Expired</option>
                <option value="cancelled">Cancelled</option>
                <option value="pending">Pending</option>
              </select>
            </div>
            <div className="relative">
              <Calendar className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="date"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-white/50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
              />
            </div>
            <div className="relative">
              <Package className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <select
                value={planFilter}
                onChange={(e) => setPlanFilter(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-white/50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 appearance-none"
              >
                <option value="all">All Plans</option>
                {getUniquePlans().map(plan => (
                  <option key={plan} value={plan}>{getPlanLabel(plan)}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Subscriptions Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredSubscriptions.map((subscription, index) => (
            <div 
              key={subscription.id}
              className="group relative rounded-xl border border-border/80 bg-card shadow-sm hover:shadow-xl transition-all duration-300 hover:scale-105 overflow-hidden"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Status indicator */}
              <div className={cn(
                'absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-medium border-2 border-white shadow-md',
                subscription.isActive && new Date(subscription.expiryDate) > new Date() ? 'bg-green-100 text-green-800' : 
                new Date(subscription.expiryDate) <= new Date() ? 'bg-red-100 text-red-800' : 
                !subscription.isActive ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'
              )}>
                {subscription.isActive && new Date(subscription.expiryDate) > new Date() ? 'Active' : 
                 new Date(subscription.expiryDate) <= new Date() ? 'Expired' : 
                 !subscription.isActive ? 'Paused' : 'Pending'}
              </div>
              
              <div className="p-6">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center">
                    <div className="w-12 h-12 rounded-xl bg-violet-500/10 flex items-center justify-center mr-3 text-violet-700 dark:text-violet-400">
                      <CreditCard className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">{subscription.customerName}</h3>
                      <p className="text-sm text-gray-600">{getPlanLabel(subscription.planType)}</p>
                    </div>
                  </div>
                </div>

                {/* Subscription Details */}
                <div className="space-y-3 mb-6">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Start Date:</span>
                    <span className="font-medium text-gray-900">{new Date(subscription.startDate).toLocaleDateString()}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Expiry Date:</span>
                    <span className="font-medium text-gray-900">{new Date(subscription.expiryDate).toLocaleDateString()}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Amount:</span>
                    <span className="font-medium text-gray-900">R{subscription.amount?.toFixed(2) || '0.00'}</span>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mb-6">
                  <div className="flex items-center justify-between text-xs text-gray-600 mb-2">
                    <span>Subscription Progress</span>
                    <span>{getSubscriptionProgress(subscription)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className={cn(
                        'h-2 rounded-full transition-all duration-300',
                        subscription.isActive && new Date(subscription.expiryDate) > new Date() ? 'bg-green-500' : 
                        new Date(subscription.expiryDate) <= new Date() ? 'bg-red-500' : 
                        !subscription.isActive ? 'bg-yellow-500' : 'bg-gray-500'
                      )}
                      style={{ width: `${getSubscriptionProgress(subscription)}%` }}
                    />
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEditSubscription(subscription)}
                      className="p-2 text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 rounded-xl transition-all duration-200"
                      title="Edit Subscription"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleToggleSubscription(subscription.id, subscription.isActive)}
                      className={`p-2 ${subscription.isActive ? 'text-yellow-600 hover:text-yellow-900 hover:bg-yellow-50' : 'text-green-600 hover:text-green-900 hover:bg-green-50'} rounded-xl transition-all duration-200`}
                      title={subscription.isActive ? 'Pause' : 'Resume'}
                    >
                      {subscription.isActive ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                    </button>
                    <button
                      onClick={() => handleCancelSubscription(subscription.id)}
                      className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-xl transition-all duration-200"
                      title="Cancel Subscription"
                    >
                      <X className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleRenewSubscription(subscription.id)}
                      className="p-2 text-green-600 hover:text-green-900 hover:bg-green-50 rounded-xl transition-all duration-200"
                      title="Renew Subscription"
                    >
                      <RefreshCw className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteSubscription(subscription.id)}
                      className="p-2 text-red-700 hover:text-red-900 hover:bg-red-50 rounded-xl transition-all duration-200"
                      title="Delete Subscription"
                    >
                      🗑️
                    </button>
                  </div>
                  
                  <span className={cn(
                    "inline-flex items-center px-3 py-1 rounded-full text-xs font-medium",
                    subscription.isActive && new Date(subscription.expiryDate) > new Date() ? "bg-green-100 text-green-800" : 
                    new Date(subscription.expiryDate) <= new Date() ? "bg-red-100 text-red-800" : 
                    !subscription.isActive ? "bg-yellow-100 text-yellow-800" : "bg-gray-100 text-gray-800"
                  )}>
                    {subscription.isActive && new Date(subscription.expiryDate) > new Date() ? 'Active' : 
                     new Date(subscription.expiryDate) <= new Date() ? 'Expired' : 
                     !subscription.isActive ? 'Paused' : 'Pending'}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {filteredSubscriptions.length === 0 && (
          <div className="text-center py-12">
            <div className="w-24 h-24 bg-muted rounded-3xl flex items-center justify-center mx-auto mb-6">
              <CreditCard className="h-12 w-12 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No subscriptions found</h3>
            <p className="text-gray-600 mb-6">
              {searchTerm || statusFilter !== 'all' || dateFilter || planFilter !== 'all'
                ? 'Try adjusting your search or filter criteria.'
                : 'Get started by adding your first subscription.'
              }
            </p>
            {!searchTerm && statusFilter === 'all' && !dateFilter && planFilter === 'all' && (
              <button
                onClick={() => setShowAddModal(true)}
                className="rounded-xl bg-primary px-6 py-3 font-semibold text-primary-foreground shadow-sm transition hover:bg-primary/90"
              >
                <Plus className="h-5 w-5 inline mr-2" />
                Add First Subscription
              </button>
            )}
          </div>
        )}
      </div>

      {/* Add/Edit Subscription Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center mb-6">
                <div className="w-10 h-10 rounded-lg bg-violet-500/10 flex items-center justify-center mr-3 text-violet-700 dark:text-violet-400">
                  <CreditCard className="h-5 w-5" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Add New Subscription</h3>
              </div>
              
              <form onSubmit={handleAddSubscription} className="space-y-4">
                {isViewingAllBranches() && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Branch *</label>
                    <select
                      required
                      value={subscriptionBranchId}
                      onChange={(e) => setSubscriptionBranchId(e.target.value)}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                    >
                      {getActualBranches().map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.name}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                      Package options below will filter to this branch (plus global packages).
                    </p>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Customer *</label>
                  <select
                    required
                    value={newSubscription.customerId}
                    onChange={(e) => setNewSubscription({ ...newSubscription, customerId: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                  >
                    <option value="">Select customer</option>
                    {customers.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name} {c.phone ? `(${c.phone})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Package *</label>
                    <select
                      required
                      value={newSubscription.planType}
                      onChange={(e) => {
                        const list = isViewingAllBranches() ? plansForSelectedBranch() : plans.filter((p) => p.isActive !== false)
                        const selectedPlan = list.find((p) => p.code === e.target.value) || list.find((p) => p.id === e.target.value)
                        setNewSubscription({ 
                          ...newSubscription, 
                          planType: e.target.value,
                          amount: selectedPlan?.amount || 0,
                          washesIncluded: selectedPlan?.washesIncluded || 'unlimited'
                        })
                      }}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                    >
                      <option value="">Select Plan</option>
                      {(isViewingAllBranches() ? plansForSelectedBranch() : plans.filter((p) => p.isActive !== false)).map((plan) => (
                        <option key={plan.id} value={plan.code || plan.id}>
                          {plan.name} {isViewingAllBranches() ? `(${branchLabel(plan.branchId)})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Amount (R) *</label>
                    <input
                      type="number"
                      required
                      min="0"
                      step="0.01"
                      value={newSubscription.amount}
                      onChange={(e) => setNewSubscription({...newSubscription, amount: parseFloat(e.target.value)})}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Start Date *</label>
                    <input
                      type="date"
                      required
                      value={newSubscription.startDate}
                      onChange={(e) => setNewSubscription({...newSubscription, startDate: e.target.value})}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Expiry Date *</label>
                    <input
                      type="date"
                      required
                      value={newSubscription.expiryDate}
                      onChange={(e) => setNewSubscription({...newSubscription, expiryDate: e.target.value})}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Status *</label>
                  <select
                    required
                    value={newSubscription.isActive}
                    onChange={(e) => setNewSubscription({...newSubscription, isActive: e.target.value === 'true'})}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                  >
                    <option value="true">Active</option>
                    <option value="false">Paused</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Notes</label>
                  <textarea
                    value={newSubscription.notes}
                    onChange={(e) => setNewSubscription({...newSubscription, notes: e.target.value})}
                    rows="3"
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 resize-none"
                    placeholder="Additional notes about this subscription..."
                  />
                </div>
                
                <div className="flex space-x-3 pt-4">
                  <button
                    type="submit"
                    className="flex-1 rounded-xl bg-primary py-3 px-4 font-semibold text-primary-foreground shadow-sm transition hover:bg-primary/90"
                  >
                    Add Subscription
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="flex-1 bg-gray-100 text-gray-700 py-3 px-4 rounded-2xl font-semibold hover:bg-gray-200 transition-all duration-300"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Add Package Modal */}
      {showPlanModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mr-3 text-primary">
                    <Package className="h-5 w-5" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">
                    {editingPlanId ? 'Edit Subscription Package' : 'Add Subscription Package'}
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setShowPlanModal(false)
                    resetPlanForm()
                  }}
                  className="p-2 rounded-xl hover:bg-gray-100 transition"
                  aria-label="Close"
                >
                  <X className="h-5 w-5 text-gray-600" />
                </button>
              </div>

              <form onSubmit={handleSavePlan} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Name *</label>
                  <input
                    type="text"
                    required
                    value={newPlan.name}
                    onChange={(e) => setNewPlan((p) => ({ ...p, name: e.target.value }))}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                    placeholder="e.g. Monthly Unlimited"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Code *</label>
                  <input
                    type="text"
                    required
                    value={newPlan.code}
                    onChange={(e) => setNewPlan((p) => ({ ...p, code: e.target.value }))}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                    placeholder="e.g. monthly-unlimited"
                  />
                  <p className="text-xs text-gray-500 mt-2">Used by POS to identify this package. Don’t change it after selling.</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Price (R) *</label>
                    <input
                      type="number"
                      required
                      min="0"
                      step="0.01"
                      value={newPlan.amount}
                      onChange={(e) => setNewPlan((p) => ({ ...p, amount: e.target.value }))}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Duration (days) *</label>
                    <input
                      type="number"
                      required
                      min="0"
                      step="1"
                      value={newPlan.durationDays}
                      onChange={(e) => setNewPlan((p) => ({ ...p, durationDays: e.target.value }))}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Washes included</label>
                  <select
                    value={newPlan.washesIncluded}
                    onChange={(e) => setNewPlan((p) => ({ ...p, washesIncluded: e.target.value }))}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                  >
                    <option value="unlimited">Unlimited</option>
                    <option value="10">10 washes</option>
                    <option value="20">20 washes</option>
                    <option value="30">30 washes</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Services & vehicle types covered
                  </label>
                  <div className="rounded-2xl border border-gray-200 bg-gray-50 p-3 max-h-56 overflow-y-auto space-y-3">
                    {washServices.map((svc) => (
                      <div key={svc.id} className="space-y-2">
                        <p className="text-sm font-semibold text-gray-900">{svc.name}</p>
                        <div className="space-y-1">
                          {(svc.tiers || []).map((t) => {
                            const key = `${svc.id}:${t.id}`
                            const checked = (newPlan.eligibleTierIds || []).includes(key)
                            return (
                              <label key={key} className="flex items-center gap-2 text-sm text-gray-800">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={(e) => {
                                    const next = new Set(newPlan.eligibleTierIds || [])
                                    if (e.target.checked) next.add(key)
                                    else next.delete(key)
                                    setNewPlan((p) => ({
                                      ...p,
                                      eligibleTierIds: Array.from(next),
                                    }))
                                  }}
                                />
                                <span className="flex-1">
                                  {t.label || t.id}
                                </span>
                                {t.price !== undefined && (
                                  <span className="text-xs text-gray-500 tabular-nums">
                                    R{Number(t.price || 0).toFixed(2)}
                                  </span>
                                )}
                              </label>
                            )
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Only selected vehicle tiers can be paid via subscription at wash-bay check-in.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
                  <select
                    value={newPlan.isActive ? 'true' : 'false'}
                    onChange={(e) => setNewPlan((p) => ({ ...p, isActive: e.target.value === 'true' }))}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                  >
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>

                <button
                  type="submit"
                  className="w-full rounded-xl bg-primary px-6 py-3 font-semibold text-primary-foreground shadow-sm transition hover:bg-primary/90"
                >
                  {editingPlanId ? 'Save Changes' : 'Create Package'}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
} 